import { Fragment } from "react";
import { Check, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStep {
  id: string;
  label: string;
  // Optional transient sub-label, e.g. "generating…" on the chat step while a
  // response streams in the background. Not part of the step's identity.
  hint?: string;
  /** Shown in the circle instead of the step number. */
  icon?: LucideIcon;
  /** Not reachable yet (e.g. a later workflow phase): greyed out and not clickable. */
  disabled?: boolean;
}

interface WizardStepperProps {
  steps: WizardStep[];
  activeIndex: number;
  completedIndices: number[];
  onStepClick?: (index: number) => void;
  /** "compact": small circles with the label beside them, in one row (page-top workflow stepper). */
  variant?: "default" | "compact";
  className?: string;
}

const circleState = (isCompleted: boolean, isActive: boolean) => {
  if (isCompleted) return "bg-brand-teal border-brand-teal text-white";
  if (isActive) return "bg-brand-teal border-brand-teal text-white ring-4 ring-brand-teal/15";
  return "bg-background border-muted-foreground/30 text-muted-foreground";
};

const CircleContent = ({ step, index, isCompleted, compact }: { step: WizardStep; index: number; isCompleted: boolean; compact: boolean }) => {
  const iconSize = compact ? "w-3.5 h-3.5" : "w-5 h-5";
  if (isCompleted) return <Check className={iconSize} />;
  if (step.icon) return <step.icon className={iconSize} />;
  return <>{index + 1}</>;
};

interface StepItemProps {
  step: WizardStep;
  index: number;
  isActive: boolean;
  isCompleted: boolean;
  compact: boolean;
  onClick?: (index: number) => void;
}

const StepItem = ({ step, index, isActive, isCompleted, compact, onClick }: StepItemProps) => {
  const isClickable = !!onClick && !step.disabled;
  return (
    <div className={cn("flex shrink-0", compact ? "items-center gap-2" : "flex-col items-center w-24")}>
      <button
        type="button"
        onClick={() => {
          if (!isActive) onClick?.(index);
        }}
        disabled={!isClickable}
        className={cn(
          "flex items-center justify-center rounded-full border-2 font-semibold transition-colors shrink-0",
          compact ? "w-7 h-7 text-xs" : "w-10 h-10 text-sm",
          circleState(isCompleted, isActive),
          isClickable ? "cursor-pointer hover:opacity-90" : "cursor-default",
          step.disabled && "opacity-60",
        )}
        aria-label={`Step ${index + 1}: ${step.label}`}
        aria-current={isActive ? "step" : undefined}
      >
        <CircleContent step={step} index={index} isCompleted={isCompleted} compact={compact} />
      </button>
      <span
        className={cn(
          "text-xs font-medium leading-tight",
          compact ? "whitespace-nowrap" : "mt-2 text-center",
          isActive ? "text-brand-navy" : "text-muted-foreground",
          step.disabled && "opacity-60",
        )}
      >
        {step.label}
      </span>
      {step.hint && (
        <span className={cn("text-[10px] leading-tight text-brand-teal", !compact && "mt-0.5 text-center")}>{step.hint}</span>
      )}
    </div>
  );
};

const WizardStepper = ({ steps, activeIndex, completedIndices, onStepClick, variant = "default", className }: WizardStepperProps) => {
  const completed = new Set(completedIndices);
  const compact = variant === "compact";

  return (
    <div className={cn("flex justify-center", compact ? "items-center" : "items-start py-6", className)}>
      {steps.map((step, idx) => (
        <Fragment key={step.id}>
          <StepItem
            step={step}
            index={idx}
            isActive={idx === activeIndex}
            isCompleted={completed.has(idx)}
            compact={compact}
            onClick={onStepClick}
          />
          {idx < steps.length - 1 && (
            <div
              className={cn(
                "h-0.5 flex-shrink-0",
                compact ? "w-12 mx-3" : "w-24 mt-5",
                completed.has(idx) ? "bg-brand-teal" : "bg-muted-foreground/20",
              )}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default WizardStepper;
